import { useEffect, useState } from "react";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";

export function useAuth() {
  const [user, setUser] = useState<import("@supabase/supabase-js").User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    if (!isSupabaseConfigured) {
      console.warn("useAuth: Supabase not configured; running in guest mode.");
      setLoading(false);
      return () => { mounted = false; };
    }
    supabase.auth.getUser().then(({ data }) => {
      if (!mounted) return;
      setUser(data.user ?? null);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setUser(session?.user ?? null);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    if (!isSupabaseConfigured) {
      return { data: null, error: new Error("Supabase not configured") };
    }
    try {
      console.log("🔐 Attempting email/password sign in for:", email);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("❌ Sign in error:", error);
        return { data: null, error };
      }

      console.log("✅ Sign in successful:", { 
        userId: data.user?.id, 
        email: data.user?.email,
        confirmed: data.user?.email_confirmed_at ? 'Yes' : 'No'
      });

      return { data, error: null };
    } catch (error) {
      console.error("💥 Unexpected sign in error:", error);
      return { data: null, error: error as Error };
    }
  };

  const signUp = async (email: string, password: string, metadata?: Record<string, any>) => {
    if (!isSupabaseConfigured) {
      return { data: null, error: new Error("Supabase not configured") };
    }
    try {
      console.log("📝 Attempting registration for:", email, "with metadata:", metadata);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata || {}
        }
      });

      if (error) {
        console.error("❌ Registration error:", error);
        return { data: null, error };
      }

      console.log("✅ Registration successful:", { 
        userId: data.user?.id, 
        email: data.user?.email,
        confirmed: data.user?.email_confirmed_at ? 'Yes' : 'No',
        needsConfirmation: !data.user?.email_confirmed_at
      });

      return { data, error: null };
    } catch (error) {
      console.error("💥 Unexpected registration error:", error);
      return { data: null, error: error as Error };
    }
  };
  const signOut = async () => {
    if (!isSupabaseConfigured) return;
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  // Password reset functionality
  const resetPassword = async (email: string) => {
    if (!isSupabaseConfigured) {
      return { error: new Error("Supabase not configured") };
    }
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        return { error };
      }

      return { data, error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  // Update password (requires current session)
  const updatePassword = async (newPassword: string) => {
    if (!isSupabaseConfigured) {
      return { error: new Error("Supabase not configured") };
    }
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        return { error };
      }

      return { data, error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  // Update email (requires current session and password confirmation)
  const updateEmail = async (newEmail: string, password: string) => {
    if (!isSupabaseConfigured) {
      return { error: new Error("Supabase not configured") };
    }
    try {
      // First verify current password
      if (user?.email) {
        const { error: verifyError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: password,
        });

        if (verifyError) {
          return { error: new Error("Current password is incorrect") };
        }
      }

      // Update email
      const { data, error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) {
        return { error };
      }

      return { data, error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  // Account recovery - find account by email
  const findAccountByEmail = async (email: string) => {
    if (!isSupabaseConfigured) {
      return { error: new Error("Supabase not configured") };
    }
    try {
      // Try to send password reset email to check if account exists
      const { error } = await supabase.auth.resetPasswordForEmail(email);

      if (error && error.message.includes("User not found")) {
        return { exists: false, error: null };
      }

      // If no error or different error, account likely exists
      return { exists: true, error: null };
    } catch (error) {
      return { exists: false, error: error as Error };
    }
  };

  // Account recovery - find accounts by phone (would need custom implementation)
  const findAccountByPhone = async (phone: string) => {
    if (!isSupabaseConfigured) {
      return { error: new Error("Supabase not configured") };
    }
    try {
      // This would require a custom function or database query
      // For now, return not implemented
      return { error: new Error("Phone recovery not yet implemented") };
    } catch (error) {
      return { error: error as Error };
    }
  };

  return { 
    user, 
    loading, 
    signUp, 
    signIn, 
    signOut,
    resetPassword,
    updatePassword,
    updateEmail,
    findAccountByEmail,
    findAccountByPhone
  };
}